import React, { useState } from "react";
import Wrapper from "./shared/Wrapper";
import { motion, AnimatePresence } from "framer-motion";
import { CirclePlay, Telescope } from "lucide-react";
import Footer from "./shared/Footer";

// Import des composants technologies
import Django from "../components/technologies/Django";
import Next from "../components/technologies/Next";
import Flutter from "../components/technologies/Flutter";
import ReactTech from "../components/technologies/React";
import CyberSecurite from "../components/technologies/CyberSecurite";
import Hebergement from "../components/technologies/hebergement";

export default function Technologies() {
    const [activeTab, setActiveTab] = useState("react");

    // Configuration des technologies avec leurs couleurs natives
    const technologies = [

        {
            id: "react",
            name: "React",
            component: ReactTech,
            gradient: "from-[#61DAFB] to-[#0088CC]",
            bgGradient: "from-blue-900/20 to-cyan-900/20",
            accentColor: "#61DAFB"
        },
        {
            id: "django",
            name: "Django",
            component: Django,
            gradient: "from-emerald-500 to-green-500",
            bgGradient: "from-blue-900/20 to-cyan-900/20",
            accentColor: "#61DAFB"
        },
        {
            id: "next",
            name: "Next.js",
            component: Next,
            gradient: "from-gray-900 to-black",
            bgGradient: "from-gray-900/20 to-black/20",
            accentColor: "#000000"
        },

        {
            id: "flutter",
            name: "Flutter",
            component: Flutter,
            gradient: "from-[#02569B] to-[#54C5F8]",
            bgGradient: "from-blue-900/20 to-sky-900/20",
            accentColor: "#02569B"
        },
        // {
        //     id: "cybersecurite",
        //     name: "Cybersécurité",
        //     component: CyberSecurite,
        //     gradient: "from-[#FF6B6B] to-[#FF8E53]",
        //     bgGradient: "from-red-900/20 to-orange-900/20",
        //     accentColor: "#FF6B6B"
        // },
        // {
        //     id: "hebergement",
        //     name: "Hébergement Cloud",
        //     component: Hebergement,
        //     gradient: "from-[#FF9900] to-[#F7B500]",
        //     bgGradient: "from-orange-900/20 to-yellow-900/20",
        //     accentColor: "#FF9900"
        // }
    ];

    const activeTech = technologies.find(tech => tech.id === activeTab);
    const ActiveComponent = activeTech?.component;

    return (
        <div className="bg-black text-white overflow-hidden">
            {/* Background fixe avec étoiles */}
            <div className="fixed inset-0 bg-gradient-to-br from-gray-900 via-black to-blue-900 z-0" />
            <div className="fixed inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(6,182,212,0.1),transparent_50%)] z-0" />

            {/* Étoiles animées */}
            <div className="fixed inset-0 overflow-hidden z-0">
                {[...Array(15)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-cyan-400/30 rounded-full"
                        initial={{
                            x: Math.random() * 100 + 'vw',
                            y: Math.random() * 100 + 'vh'
                        }}
                        animate={{
                            x: [null, Math.random() * 100 + 'vw'],
                            y: [null, Math.random() * 100 + 'vh']
                        }}
                        transition={{
                            duration: 20 + Math.random() * 10,
                            repeat: Infinity,
                            ease: "linear"
                        }}
                    />
                ))}
            </div>

            <Wrapper>
                <section className="relative overflow-hidden py-12 min-h-screen">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">

                        {/* Hero Header */}
                        <motion.div
                            initial={{ opacity: 0, y: -30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="text-center my-12"
                        >
                            <motion.h1
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6"
                            >
                                <span className="text-white">Nos</span>
                                <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-600 bg-clip-text text-transparent px-2 sm:px-4">
                                    Technologies
                                </span>
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto"
                            >
                                Découvrez notre stack technologique premium et notre expertise approfondie
                            </motion.p>
                        </motion.div>

                        {/* Navbar de sélection des technologies - Ultra moderne */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="mb-16"
                        >
                            <div className="bg-gradient-to-r from-gray-900/80 via-gray-800/80 to-gray-900/80 backdrop-blur-2xl rounded-3xl p-2 border border-gray-700/50 shadow-2xl">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                    {technologies.map((tech, index) => {
                                        const isActive = activeTab === tech.id;
                                        return (
                                            <motion.button
                                                key={tech.id}
                                                onClick={() => setActiveTab(tech.id)}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: 0.1 * index }}
                                                whileHover={{
                                                    scale: 1.05,
                                                    y: -5,
                                                    transition: { type: "spring", stiffness: 400, damping: 10 }
                                                }}
                                                whileTap={{ scale: 0.95 }}
                                                className={`relative px-6 py-4 cursor-pointer rounded-2xl font-semibold text-sm transition-all duration-300 overflow-hidden group ${isActive
                                                    ? 'text-white shadow-2xl'
                                                    : 'text-gray-400 hover:text-white'
                                                    }`}
                                            >
                                                {/* Background gradient animé pour l'onglet actif */}
                                                {isActive && (
                                                    <motion.div
                                                        layoutId="activeTabBackground"
                                                        className={`absolute inset-0 bg-gradient-to-r ${tech.gradient} rounded-2xl`}
                                                        initial={false}
                                                        transition={{
                                                            type: "spring",
                                                            stiffness: 500,
                                                            damping: 30
                                                        }}
                                                    />
                                                )}

                                                {/* Effet de brillance au survol */}
                                                <motion.div
                                                    className={`absolute inset-0 bg-gradient-to-r ${tech.gradient} opacity-0 group-hover:opacity-20 rounded-2xl transition-opacity duration-300`}
                                                />

                                                {/* Bordure animée pour l'onglet actif */}
                                                {isActive && (
                                                    <motion.div
                                                        className="absolute inset-0 rounded-2xl"
                                                        animate={{
                                                            boxShadow: [
                                                                `0 0 20px 0px ${tech.accentColor}40`,
                                                                `0 0 30px 5px ${tech.accentColor}60`,
                                                                `0 0 20px 0px ${tech.accentColor}40`
                                                            ]
                                                        }}
                                                        transition={{
                                                            duration: 2,
                                                            repeat: Infinity,
                                                            ease: "easeInOut"
                                                        }}
                                                    />
                                                )}

                                                {/* Texte */}
                                                <span className="relative z-10 block">
                                                    {tech.name}
                                                </span>

                                                {/* Indicateur actif */}
                                                {isActive && (
                                                    <motion.div
                                                        layoutId="activeIndicator"
                                                        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-white rounded-full"
                                                        initial={false}
                                                        transition={{
                                                            type: "spring",
                                                            stiffness: 500,
                                                            damping: 30
                                                        }}
                                                    />
                                                )}
                                            </motion.button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Ligne décorative sous la navbar */}
                            <motion.div
                                className="mt-8 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ delay: 0.8, duration: 0.8 }}
                            />
                        </motion.div>

                        {/* Contenu dynamique - Composant de la technologie sélectionnée */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -20, scale: 0.98 }}
                                transition={{
                                    duration: 0.5,
                                    ease: [0.25, 0.1, 0.25, 1]
                                }}
                            >
                                {ActiveComponent && <ActiveComponent />}
                            </motion.div>
                        </AnimatePresence>

                        {/* CTA Global en bas */}
                        {/* <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                            className="mt-32 text-center"
                        >
                            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full mb-6 border border-blue-500/20">
                                <CirclePlay className="w-5 h-5 text-blue-400" />
                                <span className="text-blue-400 font-medium">Prêt à démarrer ?</span>
                            </div>

                            <h3 className="text-3xl md:text-5xl font-bold text-white mb-4">
                                Développons Ensemble Votre{" "}
                                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                                    Projet Prestigieux
                                </span>
                            </h3>

                            <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-8">
                                Notre équipe d'experts est prête à transformer votre vision en réalité
                                avec les technologies les plus avancées du marché.
                            </p>

                            <motion.a
                                href="/contact"
                                whileHover={{
                                    scale: 1.05,
                                    boxShadow: "0 10px 40px rgba(6, 182, 212, 0.4)"
                                }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-xl shadow-xl transition-all duration-300 group"
                            >
                                <span>Discutons de votre projet</span>
                                <motion.div
                                    animate={{ x: [0, 5, 0] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                >
                                    <Telescope className="w-5 h-5" />
                                </motion.div>
                            </motion.a>
                        </motion.div> */}

                    </div>
                </section>

                {/* FOOTER */}
                {/* ================================================ */}
                <Footer />
            </Wrapper>
        </div>
    );
}
